import type {
  CampoFichaEquipe,
  DocumentoEquipe,
  Etapa,
  Prioridade,
  Situacao,
  TipoDemanda,
} from '@contefy/shared';
import { ETAPA_INFO, PRIORIDADE_INFO, TIPO_DEMANDA_INFO } from '@contefy/shared';
import { AlertTriangle, Check, ExternalLink, Flag, Pencil, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Botao, Selo, Spinner } from '../componentes/ui';
import { equipeApi, rotuloValor } from './api';

export function SinalSituacao({
  situacao,
  motivo,
}: {
  situacao: Situacao;
  motivo?: string | null;
}) {
  const cor = situacao === 'travada' ? 'vermelho' : situacao === 'andando' ? 'verde' : 'amarelo';
  const texto = {
    andando: 'Andando',
    aguardando_cliente: 'Esperando cliente',
    aguardando_junta: 'Esperando Junta',
    travada: 'Precisa de alguém',
  }[situacao];
  return (
    <span title={motivo ?? undefined}>
      <Selo cor={cor}>
        <span
          className={`inline-block h-2 w-2 rounded-full ${cor === 'vermelho' ? 'bg-red-500' : cor === 'verde' ? 'bg-emerald-500' : 'bg-amber-500'}`}
        />
        {texto}
      </Selo>
    </span>
  );
}

/** Bandeira de prioridade: vermelha (alta), amarela (média), verde (baixa). */
export function Bandeira({
  prioridade,
  comTexto = false,
}: {
  prioridade: Prioridade;
  comTexto?: boolean;
}) {
  const cor = { alta: 'text-red-600', media: 'text-amber-500', baixa: 'text-emerald-600' }[
    prioridade
  ];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${cor}`}
      title={PRIORIDADE_INFO[prioridade].titulo}
    >
      <Flag className="h-4 w-4 fill-current" />
      {comTexto && PRIORIDADE_INFO[prioridade].titulo}
    </span>
  );
}

export function TipoDemandaSelo({ tipo }: { tipo: TipoDemanda }) {
  return <Selo cor={tipo === 'abertura' ? 'azul' : 'cinza'}>{TIPO_DEMANDA_INFO[tipo].curto}</Selo>;
}

export function EtapaSelo({ etapa }: { etapa: Etapa }) {
  return (
    <Selo cor="azul">
      {ETAPA_INFO[etapa].numero}. {ETAPA_INFO[etapa].titulo}
    </Selo>
  );
}

export function Confianca({ valor }: { valor: number }) {
  const pct = Math.round(valor * 100);
  const cor = pct >= 85 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-slate-500"
      title={`Confiança ${pct}%`}
    >
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-200">
        <span className={`block h-full ${cor}`} style={{ width: `${pct}%` }} />
      </span>
      {pct}%
    </span>
  );
}

const ORIGEM: Record<string, string> = {
  documento: 'lido do documento',
  cliente: 'informado pelo cliente',
  ia: 'proposto pela IA',
  equipe: 'corrigido pela equipe',
  sistema: 'regra do sistema',
};

/** Linha da ficha: valor, origem, confiança, evidência; editar ou aceitar. */
export function LinhaCampo({
  campo,
  aberturaId,
  aoMudar,
  compacto = false,
}: {
  campo: CampoFichaEquipe;
  aberturaId: string;
  aoMudar: () => void;
  compacto?: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const editavel =
    campo.valor == null || ['string', 'number', 'boolean'].includes(typeof campo.valor);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      let valor: unknown = texto.trim();
      if (typeof campo.valor === 'number' && texto.trim() !== '')
        valor = Number(texto.replace(',', '.'));
      if (typeof campo.valor === 'boolean') valor = /^(s|sim|true|1)$/i.test(texto.trim());
      if (valor === '') valor = null;
      await equipeApi.corrigir(aberturaId, campo.chave, valor);
      setEditando(false);
      aoMudar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div
      className={`grid gap-2 border-b border-slate-100 py-2.5 last:border-0 sm:grid-cols-[minmax(0,1fr)_auto] ${campo.marcado ? 'bg-amber-50/70 -mx-3 px-3 rounded-lg' : ''}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-800">{campo.rotulo}</span>
          {campo.marcado && (
            <Selo cor="amarelo">
              <AlertTriangle className="h-3 w-3" /> conferir
            </Selo>
          )}
          {campo.corrigidoPor && <Selo cor="cinza">corrigido por {campo.corrigidoPor}</Selo>}
        </div>
        {editando ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              className="max-w-md"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') salvar();
                if (e.key === 'Escape') setEditando(false);
              }}
            />
            <Botao className="!min-h-9 !py-1.5" carregando={salvando} onClick={salvar}>
              Salvar
            </Botao>
            <Botao
              variante="secundario"
              className="!min-h-9 !py-1.5"
              onClick={() => setEditando(false)}
            >
              Cancelar
            </Botao>
            {erro && <span className="text-sm text-red-700">{erro}</span>}
          </div>
        ) : (
          <p className="mt-0.5 break-words text-base text-slate-900">{rotuloValor(campo.valor)}</p>
        )}
        {!compacto && (
          <p className="mt-0.5 text-xs text-slate-500">
            {ORIGEM[campo.origem] ?? campo.origem}
            {campo.motivo_marcacao && (
              <span className="text-amber-800"> · {campo.motivo_marcacao}</span>
            )}
            {campo.evidencia && (
              <span className="block text-slate-500">
                Trecho: <i>“{campo.evidencia}”</i>
              </span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-start gap-1.5">
        <Confianca valor={campo.confianca} />
        {editavel && !editando && (
          <button
            type="button"
            title="Corrigir"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            onClick={() => {
              setTexto(campo.valor == null ? '' : String(campo.valor));
              setEditando(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {campo.marcado && !editando && (
          <button
            type="button"
            title="Está certo, manter"
            className="rounded-md p-1.5 text-emerald-700 hover:bg-emerald-50"
            onClick={async () => {
              await equipeApi.aceitar(aberturaId, campo.chave);
              aoMudar();
            }}
          >
            <Check className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Abre o arquivo (imagem ou PDF) por URL temporária. */
export function VisualizadorDocumento({
  aberturaId,
  documento,
  altura = 420,
}: {
  aberturaId: string;
  documento: DocumentoEquipe;
  altura?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let ativo = true;
    setUrl(null);
    equipeApi
      .urlDocumento(aberturaId, documento.id)
      .then((r) => ativo && setUrl(r.url))
      .catch((e) => ativo && setErro((e as Error).message));
    return () => {
      ativo = false;
    };
  }, [aberturaId, documento.id]);
  if (erro) return <p className="text-sm text-red-700">{erro}</p>;
  if (!url)
    return (
      <div
        className="flex items-center justify-center rounded-lg bg-slate-100 text-slate-500"
        style={{ height: altura }}
      >
        <Spinner />
      </div>
    );
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      {documento.mime === 'application/pdf' ? (
        <iframe
          title={documento.nomeOriginal}
          src={url}
          className="w-full"
          style={{ height: altura }}
        />
      ) : (
        <img
          src={url}
          alt={documento.nomeOriginal}
          className="mx-auto max-h-[70vh] w-auto"
          style={{ maxHeight: altura }}
        />
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener"
        className="flex items-center justify-end gap-1 px-3 py-1.5 text-xs text-marca-700"
      >
        Abrir em nova aba <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

export function StatusDocumento({ status }: { status: string }) {
  const m: Record<string, ['verde' | 'amarelo' | 'vermelho' | 'cinza' | 'azul', string]> = {
    enviado: ['azul', 'Recebido'],
    extraido: ['verde', 'Lido pela IA'],
    aceito: ['verde', 'Conferido'],
    ilegivel: ['amarelo', 'Ilegível'],
    rejeitado: ['vermelho', 'Rejeitado'],
    pendente: ['cinza', 'Falta'],
  };
  const [cor, texto] = m[status] ?? ['cinza', status];
  return <Selo cor={cor}>{texto}</Selo>;
}

export function Fechar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
      title="Fechar"
    >
      <X className="h-4 w-4" />
    </button>
  );
}
